/**
 * Embedded mode: the admin panel shown inside another product's page.
 *
 * Prism shows the whole panel (e.g. `/app/dashboard?embed=prism&token=<access_token>`)
 * in an iframe. The frame URL carries `token`, an admin access token the host's backend
 * obtained, and the authGuard completes the session in place, so the login page is
 * bypassed.
 *
 * The flag is read from the URL the page loaded with and kept in sessionStorage.
 * That storage belongs to the iframe's own browsing context, so the mode survives
 * in-app navigation and reloads inside the frame while a normal top-level tab of the
 * panel is never affected. The param only counts inside a frame: a copied link opened
 * in a top-level tab gets the regular login.
 */
export type EmbedHost = 'prism';

const STORAGE_KEY = 'admin_embed_host';
const HOSTS: EmbedHost[] = ['prism'];

/** Resolved once while the app's scripts load, before the router's first navigation. */
const host: EmbedHost | null = resolve();

/**
 * The host's `token` query param: the access token of the admin the host signs in as.
 * Read from the URL the page loaded with, and only inside a frame. Null when the host
 * passed none; the frame then falls back to the normal login form.
 */
const token: string | null = host ? new URLSearchParams(window.location.search).get('token') : null;

/** The product this page is embedded in, or null when the panel runs on its own. */
export function embedHost(): EmbedHost | null {
  return host;
}

/** Embedded by any host: silent login with the host's token. */
export function isEmbedded(): boolean {
  return host !== null;
}

export function embedToken(): string | null {
  return token || null;
}

function resolve(): EmbedHost | null {
  if (!inFrame()) {
    return null;
  }
  const fromUrl = new URLSearchParams(window.location.search).get('embed');
  if (isHost(fromUrl)) {
    remember(fromUrl);
    return fromUrl;
  }
  const stored = recall();
  return isHost(stored) ? stored : null;
}

function isHost(value: string | null): value is EmbedHost {
  return HOSTS.indexOf(value as EmbedHost) !== -1;
}

function inFrame(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // A cross-origin parent blocks the comparison, which itself means "framed".
    return true;
  }
}

// sessionStorage can throw (blocked storage, some private modes); then the
// mode simply lasts for the one page load that carried the query param.
function remember(value: EmbedHost): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, value);
  } catch { /* see above */ }
}

function recall(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
